import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import DashboardPage from './pages/student/DashboardPage';
import TestListPage from './pages/student/TestListPage';
import TestPage from './pages/student/TestPage';
import ResultPage from './pages/student/ResultPage';
import ProfilePage from './pages/student/ProfilePage';
import SmartPracticePage from './pages/student/SmartPracticePage';
import PracticeSessionPage from './pages/student/PracticeSessionPage';
import PracticeSummaryPage from './pages/student/PracticeSummaryPage';
import TopicPracticePage from './pages/student/TopicPracticePage';
import PreviousYearPracticePage from './pages/student/PreviousYearPracticePage';
import AnalyticsPage from './pages/student/AnalyticsPage';
import GamificationPage from './pages/student/GamificationPage';
import SubscriptionPage from './pages/student/SubscriptionPage';
import StudyPlanPage from './pages/student/StudyPlanPage';
import OrderPage from './pages/student/OrderPage';
import OrderHistoryPage from './pages/student/OrderHistoryPage';
import ReferralPage from './pages/student/ReferralPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTests from './pages/admin/AdminTests';
import AdminQuestions from './pages/admin/AdminQuestions';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPaymentPanel from './pages/admin/AdminPaymentPanel';
import AdminFraudPanel from './pages/admin/AdminFraudPanel';
import AdminProfile from './pages/admin/AdminProfile';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import TestQuestions from './pages/admin/TestQuestions';

// Components
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={!user ? <LoginPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />} 
      />
      <Route 
        path="/admin/login" 
        element={!user ? <LoginPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />} 
      />
      <Route 
        path="/register" 
        element={!user ? <RegisterPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />} 
      />
      <Route 
        path="/forgot-password" 
        element={!user ? <ForgotPasswordPage /> : <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />} 
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Protected Student Routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tests" element={<TestListPage />} />
        <Route path="test/:id" element={<TestPage />} />
        <Route path="test-result/:attemptId" element={<ResultPage />} />
        <Route path="practice" element={<SmartPracticePage />} />
        <Route path="practice/session" element={<PracticeSessionPage />} />
        <Route path="practice/summary" element={<PracticeSummaryPage />} />
        <Route path="practice/topic/:subject/:topic" element={<TopicPracticePage />} />
        <Route path="practice/previous-year/:subject/:year" element={<PreviousYearPracticePage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="progress" element={<GamificationPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="study-plan" element={<StudyPlanPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="order/:orderId" element={<OrderPage />} />
        <Route path="order-history" element={<OrderHistoryPage />} />
        <Route path="referral" element={<ReferralPage />} />
      </Route>

      {/* Protected Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="tests" element={<AdminTests />} />
        <Route path="tests/:testId/questions" element={<TestQuestions />} />
        <Route path="questions" element={<AdminQuestions />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="payments" element={<AdminPaymentPanelRoute />} />
        <Route path="fraud" element={<AdminFraudPanel />} />
        <Route path="plans" element={<AdminPlansPage />} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (user.role === 'admin') {
    return <Navigate to="/admin" />;
  }
  
  return <>{children}</>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
}

// Wrapper to pass adminId from auth context into AdminPaymentPanel
function AdminPaymentPanelRoute() {
  const { user } = useAuth();
  return <AdminPaymentPanel adminId={user!.id} />;
}

export default App;